import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Envelope,
  ArrowsClockwise,
  CheckCircle,
  Warning,
  Clock,
  CircleNotch,
  Gear,
  X,
  Trash,
  Eye,
  ArrowDown,
} from "@phosphor-icons/react";
import { useAutoRefresh } from "../../hooks/useAutoRefresh";

const API = `${window.location.origin}/api`;

/**
 * TagListInput — chips-style multi-value input. Values persist on Enter,
 * comma, or blur. Click × on a chip to remove. Optional autocomplete
 * `suggestions` renders under the input.
 */
function TagListInput({ values = [], onChange, placeholder = "", suggestions = [], testIdPrefix = "tag-input" }) {
  const [draft, setDraft] = React.useState("");
  const arr = Array.isArray(values) ? values : [];
  const commit = (raw) => {
    const parts = String(raw || "")
      .split(/[,\n\r\t;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    const next = [...arr];
    for (const p of parts) if (!next.includes(p)) next.push(p);
    if (next.length !== arr.length) onChange(next);
    setDraft("");
  };
  const removeAt = (idx) => {
    const next = arr.filter((_, i) => i !== idx);
    onChange(next);
  };
  const unusedSuggestions = (suggestions || []).filter((s) => s && !arr.includes(s));
  return (
    <div className="min-h-[40px] w-full border border-border rounded-md bg-white p-1.5 flex flex-wrap items-center gap-1.5 focus-within:ring-2 focus-within:ring-blue-100">
      {arr.map((v, i) => (
        <span
          key={`${v}-${i}`}
          className="inline-flex items-center gap-1 rounded-md bg-blue-50 text-blue-800 border border-blue-200 px-2 py-0.5 text-xs font-medium"
          data-testid={`${testIdPrefix}-chip-${i}`}
        >
          <span className="max-w-[240px] truncate">{v}</span>
          <button
            type="button"
            aria-label={`Usuń ${v}`}
            onClick={() => removeAt(i)}
            className="hover:text-red-600 shrink-0"
            data-testid={`${testIdPrefix}-remove-${i}`}
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "," || e.key === ";") {
            e.preventDefault();
            commit(draft);
          } else if (e.key === "Backspace" && !draft && arr.length) {
            e.preventDefault();
            removeAt(arr.length - 1);
          }
        }}
        onBlur={() => commit(draft)}
        placeholder={arr.length ? "" : placeholder}
        className="flex-1 min-w-[120px] h-7 px-2 text-sm outline-none bg-transparent"
        data-testid={`${testIdPrefix}-input`}
      />
      {draft.trim() && (
        <button
          type="button"
          onClick={() => commit(draft)}
          className="text-[10px] px-2 py-0.5 rounded bg-blue-600 hover:bg-blue-700 text-white"
          data-testid={`${testIdPrefix}-add`}
        >
          + Dodaj
        </button>
      )}
      {unusedSuggestions.length > 0 && (
        <div className="basis-full flex flex-wrap gap-1 pt-1 border-t border-slate-100 mt-1">
          {unusedSuggestions.slice(0, 12).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => commit(s)}
              className="text-[10px] px-2 py-0.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
              data-testid={`${testIdPrefix}-suggest-${s}`}
              title="Kliknij, aby dodać"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const STATUS_STYLES = {
  NEW: "bg-slate-100 text-slate-700",
  DOWNLOADING: "bg-blue-50 text-blue-700",
  DOWNLOADED: "bg-blue-50 text-blue-700",
  PROCESSING: "bg-amber-50 text-amber-700",
  PROCESSED: "bg-emerald-50 text-emerald-700",
  ERROR: "bg-red-50 text-red-700",
};

const STATUS_LABEL = {
  NEW: "Nowy",
  DOWNLOADING: "Pobieranie",
  DOWNLOADED: "Pobrano",
  PROCESSING: "Przetwarzanie",
  PROCESSED: "Przetworzono",
  ERROR: "Błąd",
};

const DOC_TYPE_LABEL = {
  kurier: "Kurier",
  przewoznik: "Przewoźnik → Dochód",
  unknown: "Nie rozpoznano",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || "bg-slate-100 text-slate-700";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold ${cls}`}
      data-testid={`dpd-status-${status.toLowerCase()}`}
    >
      {status === "PROCESSED" && <CheckCircle className="w-3 h-3" weight="fill" />}
      {status === "ERROR" && <Warning className="w-3 h-3" weight="fill" />}
      {(status === "DOWNLOADING" || status === "PROCESSING") && (
        <CircleNotch className="w-3 h-3 animate-spin" />
      )}
      {(status === "NEW" || status === "DOWNLOADED") && <Clock className="w-3 h-3" />}
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function StatCard({ label, value, tone = "default", testId }) {
  const toneCls = {
    default: "border-border bg-white",
    success: "border-emerald-200 bg-emerald-50",
    danger: "border-red-200 bg-red-50",
    info: "border-blue-200 bg-blue-50",
  }[tone];
  return (
    <div className={`px-4 py-3 border rounded-md ${toneCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">
        {label}
      </div>
      <div className="text-xl font-bold text-foreground mt-0.5">{value}</div>
    </div>
  );
}

export default function DpdImportSection() {
  const [status, setStatus] = useState({ counters: {}, documents: [], messages: [] });
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastScanSummary, setLastScanSummary] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [assigningId, setAssigningId] = useState(null);
  const [assignKurier, setAssignKurier] = useState("");
  const [error, setError] = useState(null);
  const [availableFolders, setAvailableFolders] = useState(null);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [importing, setImporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, cfg] = await Promise.all([
        axios.get(`${API}/dpd-import/status`),
        axios.get(`${API}/dpd-import/settings`),
      ]);
      setStatus(s.data);
      setSettings(cfg.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Refresh only when tab regains focus / user clicks manual refresh.
  // No interval polling — otherwise selecting checkboxes / typing would
  // be interrupted by silent reloads.
  useAutoRefresh(fetchAll);

  const runScan = async () => {
    setScanning(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/dpd-import/scan`);
      setLastScanSummary(data);
      await fetchAll();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setScanning(false);
    }
  };

  const retryDoc = async (docId) => {
    try {
      await axios.post(`${API}/dpd-import/documents/${docId}/retry`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Usunąć wpis z listy importu?")) return;
    try {
      await axios.delete(`${API}/dpd-import/documents/${docId}`);
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const submitAssign = async () => {
    if (!assigningId || !assignKurier.trim()) return;
    try {
      await axios.post(`${API}/dpd-import/documents/${assigningId}/assign-kurier`, {
        kurier: assignKurier.trim(),
      });
      setAssigningId(null);
      setAssignKurier("");
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const resetHistory = async () => {
    if (!window.confirm(
      "Wyczyścić historię zaimportowanych wiadomości i dokumentów?\n" +
      "Wypłaty i dochód firmy pozostają nietknięte. Następny skan pobierze wszystko od nowa."
    )) return;
    try {
      const { data } = await axios.post(`${API}/dpd-import/reset`);
      alert(`Wyczyszczono: ${data.deleted_messages} wiadomości, ${data.deleted_documents} dokumentów`);
      setLastScanSummary(null);
      setSelected(new Set());
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const openPdf = (docId) => {
    const token = localStorage.getItem("access_token");
    const url = `${API}/dpd-import/documents/${docId}/file?token=${encodeURIComponent(token || "")}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toggleSelect = (docId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  const importableDocs = (status.documents || []).filter(
    (d) => d.status === "DOWNLOADED" && d.doc_type !== "unknown"
  );

  // Extract the specyfikacja date (YYYY-MM-DD) from the filename. Falls back
  // to imported_at date, then "Bez daty".
  const groupKey = (d) => {
    const m = /(\d{4}-\d{2}-\d{2})/.exec(d.filename || "");
    if (m) return m[1];
    if (d.imported_at) return d.imported_at.slice(0, 10);
    return "Bez daty";
  };

  // Group documents by date, keep groups sorted DESC (newest first) and rows
  // within a group sorted by filename asc for stable ordering.
  const groupedDocs = (() => {
    const map = new Map();
    for (const d of status.documents || []) {
      const k = groupKey(d);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    }
    const groups = Array.from(map.entries()).map(([date, docs]) => ({
      date,
      docs: [...docs].sort((a, b) => (a.filename || "").localeCompare(b.filename || "")),
      selectableIds: docs
        .filter(
          (d) => d.status === "DOWNLOADED" && d.doc_type !== "unknown"
        )
        .map((d) => d.id),
    }));
    groups.sort((a, b) => b.date.localeCompare(a.date));
    return groups;
  })();

  const toggleGroup = (ids) => {
    const idsSet = new Set(ids);
    const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        idsSet.forEach((id) => next.delete(id));
      } else {
        idsSet.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === importableDocs.length && importableDocs.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(importableDocs.map((d) => d.id)));
    }
  };

  const batchImport = async () => {
    if (selected.size === 0) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/dpd-import/documents/batch-import`, {
        doc_ids: Array.from(selected),
      });
      const withDoplata = (data.details || []).filter(
        (d) => d.status === "PROCESSED" && (Number(d.doplata_do_auta) > 0 || d.doplata_do_auta_warning)
      );
      let msg = `Import zakończony:\n  Przetworzone: ${data.processed}\n  Błędy: ${data.errors}`;
      if (withDoplata.length > 0) {
        msg += `\n\n⚠️ Dopłata do auta (odjęta od wynagrodzenia) w ${withDoplata.length} dokumentach:\n`;
        withDoplata.forEach((d) => {
          msg += Number(d.doplata_do_auta) > 0
            ? `  • ${d.filename} → ${d.kurier || "?"}: −${Number(d.doplata_do_auta).toFixed(2)} zł\n`
            : `  • ${d.filename} → ${d.kurier || "?"}: ${d.doplata_do_auta_warning}\n`;
        });
      }
      alert(msg);
      setSelected(new Set());
      await fetchAll();
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    } finally {
      setImporting(false);
    }
  };

  const saveSettings = async (patch) => {
    try {
      const { data } = await axios.put(`${API}/dpd-import/settings`, patch);
      setSettings(data);
    } catch (e) {
      alert(e.response?.data?.detail || e.message);
    }
  };

  const loadFolders = async () => {
    setLoadingFolders(true);
    try {
      const { data } = await axios.get(`${API}/dpd-import/folders`);
      setAvailableFolders(data.folders || []);
    } catch (e) {
      alert(
        (e.response?.data?.detail || e.message) +
          "\nNajpierw wybierz skrzynkę pocztową."
      );
    } finally {
      setLoadingFolders(false);
    }
  };

  const counters = status.counters || {};

  return (
    <div className="space-y-6" data-testid="dpd-import-section">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Envelope className="w-5 h-5 text-slate-500" weight="duotone" />
            <h2 className="text-lg font-bold text-foreground">Import DPD z Titan Mail</h2>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Automatycznie pobiera specyfikacje kurierów oraz przewoźników z Twojej skrzynki
            i przypisuje je do wypłat.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={resetHistory}
            className="h-9 px-3 flex items-center gap-1.5 border border-red-200 text-red-700 rounded-md text-sm hover:bg-red-50"
            title="Wyczyść historię — kolejny skan pobierze wszystko od nowa"
            data-testid="dpd-reset-btn"
          >
            <Trash className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={() => setShowSettings((v) => !v)}
            className="h-9 px-3 flex items-center gap-1.5 border border-border rounded-md text-sm text-muted-foreground hover:bg-muted"
            data-testid="dpd-settings-toggle"
          >
            <Gear className="w-4 h-4" />
            Ustawienia
          </button>
          <button
            onClick={runScan}
            disabled={scanning || !settings?.titan_configured}
            className="h-9 px-4 flex items-center gap-2 rounded-md bg-[#2563EB] text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dpd-scan-btn"
          >
            {scanning ? (
              <>
                <CircleNotch className="w-4 h-4 animate-spin" />
                Sprawdzam…
              </>
            ) : (
              <>
                <ArrowsClockwise className="w-4 h-4" />
                Sprawdź nowe wiadomości
              </>
            )}
          </button>
        </div>
      </div>

      {!settings?.titan_configured && (
        <div className="border border-amber-300 bg-amber-50 text-amber-900 rounded-md px-4 py-3 text-sm" data-testid="dpd-titan-not-configured">
          <div className="flex items-start gap-2">
            <Warning className="w-5 h-5 shrink-0 mt-0.5" weight="fill" />
            <div>
              <div className="font-semibold">Skrzynka pocztowa nie jest skonfigurowana</div>
              <div className="mt-1">
                Otwórz <b>Ustawienia</b> powyżej i wybierz skrzynkę z listy, albo dodaj
                nową w zakładce <b>Poczta</b>. Alternatywnie ustaw{" "}
                <code className="bg-white/70 px-1 rounded">TITAN_EMAIL</code> /{" "}
                <code className="bg-white/70 px-1 rounded">TITAN_PASSWORD</code> w{" "}
                <code className="bg-white/70 px-1 rounded">/app/backend/.env</code>.
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="border border-red-300 bg-red-50 text-red-900 rounded-md px-4 py-3 text-sm" data-testid="dpd-error">
          <div className="flex items-start justify-between gap-2">
            <div>{error}</div>
            <button onClick={() => setError(null)}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Settings panel */}
      {showSettings && settings && (
        <div className="border border-border rounded-md p-4 bg-slate-50" data-testid="dpd-settings-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Skrzynki pocztowe (z zakładki Poczta) — można wybrać kilka
              </div>
              <div className="border border-border rounded-md bg-white divide-y divide-slate-100 max-h-48 overflow-y-auto" data-testid="dpd-setting-mailboxes">
                {(settings.mailboxes || []).length === 0 ? (
                  <div className="p-3 text-xs text-amber-700">
                    Brak skonfigurowanych skrzynek. Dodaj skrzynkę w zakładce <b>Poczta</b> (np. Titan, Gmail).
                  </div>
                ) : (
                  (settings.mailboxes || []).map((m) => {
                    const selectedIds = new Set(settings.mailbox_ids || (settings.mailbox_id ? [settings.mailbox_id] : []));
                    const checked = selectedIds.has(m.id);
                    return (
                      <label key={m.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = new Set(selectedIds);
                            if (checked) next.delete(m.id); else next.add(m.id);
                            saveSettings({ mailbox_ids: Array.from(next) });
                          }}
                          className="w-4 h-4"
                          data-testid={`dpd-mailbox-toggle-${m.id}`}
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold truncate">{m.name}</div>
                          <div className="text-[10px] text-muted-foreground truncate">
                            {m.email} · {m.imap_host || "?"}{m.active === false ? " · nieaktywna" : ""}
                          </div>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Zaznacz wszystkie skrzynki, w których mogą pojawiać się specyfikacje DPD (np. Titan biuro + Gmail księgowej). Skan przechodzi po każdej po kolei i deduplikuje wiadomości.
              </div>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Adresy nadawców DPD (można dodać kilka)
              </div>
              <TagListInput
                values={(settings.senders && settings.senders.length ? settings.senders : (settings.sender ? [settings.sender] : []))}
                onChange={(next) => saveSettings({ senders: next })}
                placeholder="np. specyfikacjerdk_nsb@dpd.com.pl"
                testIdPrefix="dpd-setting-sender"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Dodaj każdy adres osobno, np. <code>specyfikacjerdk_nsb@dpd.com.pl</code>, <code>noreply@dpd.com.pl</code>. Skan przechodzi po każdym po kolei.
              </div>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1 flex items-center justify-between">
                <span>Foldery IMAP (można dodać kilka)</span>
                <button
                  type="button"
                  onClick={loadFolders}
                  disabled={loadingFolders}
                  className="text-[10px] text-blue-600 hover:underline disabled:opacity-50"
                  data-testid="dpd-fetch-folders"
                >
                  {loadingFolders ? "Ładuję…" : "Pobierz listę"}
                </button>
              </div>
              <TagListInput
                values={(settings.imap_folders && settings.imap_folders.length ? settings.imap_folders : (settings.imap_folder ? [settings.imap_folder] : ["INBOX"]))}
                onChange={(next) => saveSettings({ imap_folders: next })}
                placeholder='INBOX, specyfikacje, DPD/archiwum, lub "*"'
                suggestions={availableFolders || []}
                testIdPrefix="dpd-setting-folder"
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                Domyślnie <code>INBOX</code>. Dodaj podfoldery (np. <code>specyfikacje</code>, <code>DPD/archiwum</code>) lub wpisz <code>*</code> aby przeskanować wszystkie foldery.
              </div>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Automatyczne sprawdzanie
              </div>
              <select
                value={String(settings.autosync_minutes ?? 0)}
                onChange={(e) => saveSettings({ autosync_minutes: Number(e.target.value) })}
                className="w-full h-9 px-3 border border-border rounded-md text-sm bg-white"
                data-testid="dpd-setting-autosync"
              >
                <option value="0">Wyłączone (tylko ręcznie)</option>
                <option value="15">Co 15 minut</option>
                <option value="30">Co 30 minut</option>
                <option value="60">Co godzinę</option>
                <option value="180">Co 3 godziny</option>
              </select>
            </label>
            <label className="block">
              <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                Skanuj wiadomości nie starsze niż
              </div>
              <select
                value={String(settings.max_age_days ?? 14)}
                onChange={(e) => saveSettings({ max_age_days: Number(e.target.value) })}
                className="w-full h-9 px-3 border border-border rounded-md text-sm bg-white"
                data-testid="dpd-setting-max-age"
              >
                <option value="7">7 dni</option>
                <option value="14">14 dni (zalecane)</option>
                <option value="30">30 dni</option>
                <option value="60">60 dni</option>
                <option value="0">Bez limitu (może dużo błędów SAS expired)</option>
              </select>
              <div className="text-[10px] text-muted-foreground mt-1">
                Linki Azure Blob w wiadomościach DPD wygasają po ok. 2-4 tygodniach.
              </div>
            </label>
          </div>
          <div className="text-xs text-muted-foreground mt-3">
            {(settings.selected_mailboxes || []).length > 0 ? (
              <>
                Skanowane skrzynki ({settings.selected_mailboxes.length}):{" "}
                {settings.selected_mailboxes.map((m, i) => (
                  <span key={m.id}>
                    <b>{m.name}</b>{" "}
                    <code className="text-[10px]">
                      {m.imap_host}:{m.imap_port}
                    </code>
                    {i < settings.selected_mailboxes.length - 1 ? ", " : ""}
                  </span>
                ))}
              </>
            ) : settings.titan_env_configured ? (
              <>Używane: env <code>TITAN_EMAIL</code> · <code>{settings.titan_host}:{settings.titan_port}</code></>
            ) : (
              <span className="text-amber-700">Brak konfiguracji — zaznacz przynajmniej jedną skrzynkę powyżej.</span>
            )}
          </div>
        </div>
      )}

      {/* Counters */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <StatCard label="Wiadomości" value={counters.total_messages ?? 0} testId="dpd-cnt-messages" />
        <StatCard label="Dokumenty" value={counters.total_documents ?? 0} testId="dpd-cnt-total" />
        <StatCard label="Do importu" value={counters.downloaded_pending ?? 0} tone="info" testId="dpd-cnt-pending" />
        <StatCard label="Zaimportowane" value={counters.processed ?? 0} tone="success" testId="dpd-cnt-processed" />
        <StatCard label="W trakcie" value={counters.in_progress ?? 0} testId="dpd-cnt-inprogress" />
        <StatCard label="Błędy" value={counters.errors ?? 0} tone="danger" testId="dpd-cnt-errors" />
      </div>

      {/* Last scan summary */}
      {lastScanSummary && (
        <div className="border border-blue-200 bg-blue-50 text-blue-900 rounded-md px-4 py-3 text-sm" data-testid="dpd-last-scan">
          <div className="font-semibold mb-1">Ostatni skan (pobieranie plików)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div>Wiadomości: <b>{lastScanSummary.messages_found}</b></div>
            <div>Nowe: <b>{lastScanSummary.messages_new}</b></div>
            <div>Znalezione PDF: <b>{lastScanSummary.pdf_links_found}</b></div>
            <div>Pobrane: <b>{lastScanSummary.pdf_downloaded}</b></div>
            <div>Błędy: <b>{lastScanSummary.pdf_errors}</b></div>
          </div>
          <div className="mt-2 text-xs">
            Zaznacz dokumenty w tabeli i kliknij <b>„Importuj wybrane"</b> aby przypisać je do wypłat.
          </div>
        </div>
      )}

      {/* Documents table */}
      <div className="border border-border rounded-md overflow-hidden bg-white">
        <div className="px-4 py-2 border-b border-border bg-slate-50 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
            Ostatnie dokumenty
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Zaznaczone: <b>{selected.size}</b> z <b>{importableDocs.length}</b> gotowych
            </span>
            <button
              onClick={batchImport}
              disabled={selected.size === 0 || importing}
              className="h-8 px-3 flex items-center gap-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="dpd-batch-import-btn"
            >
              {importing ? (
                <>
                  <CircleNotch className="w-3.5 h-3.5 animate-spin" />
                  Importuję…
                </>
              ) : (
                <>
                  <ArrowDown className="w-3.5 h-3.5" />
                  Importuj wybrane ({selected.size})
                </>
              )}
            </button>
          </div>
        </div>
        {loading && status.documents.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Ładowanie…</div>
        ) : status.documents.length === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Brak dokumentów. Kliknij „Sprawdź nowe wiadomości" aby pobrać specyfikacje.
          </div>
        ) : (
          <>
            {/* Desktop / tablet: table view */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={selected.size > 0 && selected.size === importableDocs.length}
                    onChange={toggleSelectAll}
                    disabled={importableDocs.length === 0}
                    aria-label="Zaznacz wszystkie"
                    data-testid="dpd-select-all"
                  />
                </th>
                <th className="text-left px-4 py-2">Plik</th>
                <th className="text-left px-4 py-2">Typ</th>
                <th className="text-left px-4 py-2">Kurier</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Wynik</th>
                <th className="text-right px-4 py-2">Akcje</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {groupedDocs.map((group) => {
                const groupAllSelected =
                  group.selectableIds.length > 0 &&
                  group.selectableIds.every((id) => selected.has(id));
                const groupSelectedCount = group.selectableIds.filter((id) => selected.has(id)).length;
                return (
                  <React.Fragment key={group.date}>
                    <tr
                      className="bg-slate-100 hover:bg-slate-200 sticky top-0"
                      data-testid={`dpd-group-${group.date}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={groupAllSelected}
                          onChange={() => toggleGroup(group.selectableIds)}
                          disabled={group.selectableIds.length === 0}
                          aria-label={`Zaznacz grupę ${group.date}`}
                          data-testid={`dpd-group-select-${group.date}`}
                        />
                      </td>
                      <td colSpan={6} className="px-4 py-2 text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span>{group.date}</span>
                          <span className="text-[11px] font-normal text-slate-500 normal-case tracking-normal">
                            {group.docs.length}{" "}
                            {group.docs.length === 1 ? "dokument" : "dokumenty"}
                            {group.selectableIds.length > 0
                              ? ` · gotowych do importu: ${group.selectableIds.length}${groupSelectedCount > 0 ? ` (zaznaczono ${groupSelectedCount})` : ""}`
                              : ""}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.docs.map((d) => {
                      const canImport = d.status === "DOWNLOADED" && d.doc_type !== "unknown";
                      const canPreview = ["DOWNLOADED", "PROCESSING", "PROCESSED"].includes(d.status);
                      return (
                        <tr key={d.id} className="hover:bg-slate-50" data-testid={`dpd-doc-row-${d.id}`}>
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={selected.has(d.id)}
                              onChange={() => toggleSelect(d.id)}
                              disabled={!canImport}
                              aria-label="Zaznacz"
                              data-testid={`dpd-select-${d.id}`}
                            />
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">
                            <div className="truncate max-w-[280px]" title={d.filename}>{d.filename}</div>
                            <div className="text-[10px] text-muted-foreground">
                              {(d.size_bytes / 1024).toFixed(0)} KB
                              {d.imported_at ? ` · ${new Date(d.imported_at).toLocaleString("pl-PL")}` : ""}
                            </div>
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {DOC_TYPE_LABEL[d.doc_type] || d.doc_type}
                          </td>
                          <td className="px-4 py-2 font-mono text-xs">{d.kurier || "—"}</td>
                          <td className="px-4 py-2">
                            <StatusBadge status={d.status} />
                          </td>
                          <td className="px-4 py-2 text-xs">
                            {d.status === "PROCESSED" ? (
                              <span className="text-emerald-700">{d.result_summary || "OK"}</span>
                            ) : d.error ? (
                              <span className="text-red-700 line-clamp-2" title={d.error}>{d.error}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            {canPreview && (
                              <button
                                onClick={() => openPdf(d.id)}
                                className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1 mr-3"
                                title="Otwórz PDF w nowej karcie"
                                data-testid={`dpd-open-${d.id}`}
                              >
                                <Eye className="w-3.5 h-3.5" />
                                Otwórz
                              </button>
                            )}
                            {d.status === "ERROR" && d.doc_type === "unknown" && (
                              <button
                                onClick={() => {
                                  setAssigningId(d.id);
                                  setAssignKurier("");
                                }}
                                className="text-xs text-blue-600 hover:underline mr-3"
                                data-testid={`dpd-assign-${d.id}`}
                              >
                                Przypisz kuriera
                              </button>
                            )}
                            {d.status === "ERROR" && d.doc_type !== "unknown" && (
                              <button
                                onClick={() => retryDoc(d.id)}
                                className="text-xs text-blue-600 hover:underline mr-3"
                                data-testid={`dpd-retry-${d.id}`}
                              >
                                Ponów
                              </button>
                            )}
                            <button
                              onClick={() => deleteDoc(d.id)}
                              className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                              data-testid={`dpd-delete-${d.id}`}
                            >
                              <Trash className="w-3 h-3" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
            </div>

            {/* Mobile: card view */}
            <div className="md:hidden divide-y divide-border">
              {groupedDocs.map((group) => {
                const groupAllSelected =
                  group.selectableIds.length > 0 &&
                  group.selectableIds.every((id) => selected.has(id));
                const groupSelectedCount = group.selectableIds.filter((id) => selected.has(id)).length;
                return (
                  <div key={`m-${group.date}`} data-testid={`dpd-group-mobile-${group.date}`}>
                    <div className="bg-slate-100 px-4 py-2 flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={groupAllSelected}
                        onChange={() => toggleGroup(group.selectableIds)}
                        disabled={group.selectableIds.length === 0}
                        aria-label={`Zaznacz grupę ${group.date}`}
                        data-testid={`dpd-group-select-mobile-${group.date}`}
                        className="w-5 h-5"
                      />
                      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
                        {group.date}
                        <span className="ml-2 text-[11px] font-normal text-slate-500 normal-case tracking-normal">
                          {group.docs.length}
                          {group.selectableIds.length > 0
                            ? ` · gotowych: ${group.selectableIds.length}${groupSelectedCount > 0 ? ` (✓${groupSelectedCount})` : ""}`
                            : ""}
                        </span>
                      </div>
                    </div>
                    {group.docs.map((d) => {
                      const canImport = d.status === "DOWNLOADED" && d.doc_type !== "unknown";
                      const canPreview = ["DOWNLOADED", "PROCESSING", "PROCESSED"].includes(d.status);
                      return (
                        <div key={`m-${d.id}`} className="px-4 py-3 flex items-start gap-3" data-testid={`dpd-doc-card-${d.id}`}>
                          <input
                            type="checkbox"
                            checked={selected.has(d.id)}
                            onChange={() => toggleSelect(d.id)}
                            disabled={!canImport}
                            aria-label="Zaznacz"
                            data-testid={`dpd-select-mobile-${d.id}`}
                            className="w-5 h-5 mt-1 shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-mono text-foreground break-all mb-1" title={d.filename}>
                              {d.filename}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground mb-1">
                              <span>{DOC_TYPE_LABEL[d.doc_type] || d.doc_type}</span>
                              {d.kurier && <span className="font-mono">· {d.kurier}</span>}
                              <span>· {(d.size_bytes / 1024).toFixed(0)} KB</span>
                            </div>
                            <div className="mb-2"><StatusBadge status={d.status} /></div>
                            {d.status === "PROCESSED" && d.result_summary && (
                              <div className="text-[11px] text-emerald-700 mb-2">{d.result_summary}</div>
                            )}
                            {d.error && (
                              <div className="text-[11px] text-red-700 mb-2 line-clamp-3" title={d.error}>{d.error}</div>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              {canPreview && (
                                <button
                                  onClick={() => openPdf(d.id)}
                                  className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1"
                                  data-testid={`dpd-open-mobile-${d.id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                  Otwórz
                                </button>
                              )}
                              {d.status === "ERROR" && d.doc_type === "unknown" && (
                                <button
                                  onClick={() => { setAssigningId(d.id); setAssignKurier(""); }}
                                  className="text-xs text-blue-600 hover:underline"
                                  data-testid={`dpd-assign-mobile-${d.id}`}
                                >
                                  Przypisz kuriera
                                </button>
                              )}
                              {d.status === "ERROR" && d.doc_type !== "unknown" && (
                                <button
                                  onClick={() => retryDoc(d.id)}
                                  className="text-xs text-blue-600 hover:underline"
                                  data-testid={`dpd-retry-mobile-${d.id}`}
                                >
                                  Ponów
                                </button>
                              )}
                              <button
                                onClick={() => deleteDoc(d.id)}
                                className="text-xs text-red-600 hover:underline inline-flex items-center gap-1 ml-auto"
                                data-testid={`dpd-delete-mobile-${d.id}`}
                              >
                                <Trash className="w-3.5 h-3.5" />
                                Usuń
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Assign-kurier modal */}
      {assigningId && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4"
          data-testid="dpd-assign-modal"
        >
          <div className="bg-white rounded-md shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-foreground">Przypisz kuriera</h3>
              <button onClick={() => setAssigningId(null)}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-3">
              Nie udało się rozpoznać kuriera z nazwy pliku. Wpisz skrót pracownika (np. A6D):
            </p>
            <input
              type="text"
              value={assignKurier}
              onChange={(e) => setAssignKurier(e.target.value)}
              autoFocus
              className="w-full h-10 px-3 border border-border rounded-md text-sm mb-4 uppercase"
              placeholder="A6D"
              data-testid="dpd-assign-input"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setAssigningId(null)}
                className="h-9 px-4 rounded-md border border-border text-sm hover:bg-muted"
              >
                Anuluj
              </button>
              <button
                onClick={submitAssign}
                disabled={!assignKurier.trim()}
                className="h-9 px-4 rounded-md bg-[#2563EB] text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50"
                data-testid="dpd-assign-submit"
              >
                Przypisz i przetwórz
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
